import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeContext, type ResolvedTheme, type Theme } from '@/hooks/useTheme';

const STORAGE_KEY = 'dfupr-theme';

const readStoredTheme = (): Theme => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
};

const systemPrefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolve = (theme: Theme, systemDark: boolean): ResolvedTheme =>
  theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

/**
 * Класс .dark ставится сразу, до перерисовки: компоненты, читающие CSS-переменные при рендере
 * (графики), получают уже новые цвета. Анимации на миг отключаются, чтобы цвета не «переливались».
 */
const applyTheme = (resolved: ResolvedTheme) => {
  const root = document.documentElement;
  if (root.classList.contains('dark') === (resolved === 'dark')) return;
  root.classList.add('[&_*]:!transition-none');
  root.classList.toggle('dark', resolved === 'dark');
  window.setTimeout(() => root.classList.remove('[&_*]:!transition-none'), 0);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);
  const themeRef = useRef(theme);
  themeRef.current = theme;

  // Следим за системной темой, когда выбран режим «Как в системе»
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      if (themeRef.current === 'system') applyTheme(event.matches ? 'dark' : 'light');
      setSystemDark(event.matches);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = resolve(theme, systemDark);

  // Страховка: класс всегда соответствует теме (например, после первой загрузки)
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: Theme) => {
    applyTheme(resolve(next, systemPrefersDark()));
    setThemeState(next);
    try {
      if (next === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, next);
      }
    } catch {
      // Хранилище недоступно (приватный режим) — тема действует до перезагрузки
    }
  }, []);

  const value = useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
